"use client";

import { m as motion } from "framer-motion";
import { ArrowRight, Zap } from "lucide-react";

interface EngineeringProfileHeroVisualProps {
  isItalian: boolean;
}

const stack = [
  { label: "Next.js", level: 5 },
  { label: "React", level: 5 },
  { label: "TypeScript", level: 5 },
  { label: "Python", level: 5 },
  { label: "PostgreSQL", level: 5 },
  { label: "Docker", level: 5 },
  { label: "Flutter", level: 4 },
  { label: "Angular", level: 3.5 },
  { label: "Node.js", level: 2.5 },
];

const timeline = [
  {
    yearIt: "mag 2021 - gen 2026",
    yearEn: "May 2021 - Jan 2026",
    companyIt: "Accenture",
    companyEn: "Accenture",
    roleIt: "Full Stack Developer",
    roleEn: "Full Stack Developer",
    tagIt: "Enterprise",
    tagEn: "Enterprise",
    done: true,
  },
  {
    yearIt: "gen 2026 → presente",
    yearEn: "Jan 2026 → present",
    companyIt: "Dimensione 4",
    companyEn: "Dimensione 4",
    roleIt: "Freelance Full Stack",
    roleEn: "Freelance Full Stack",
    tagIt: "Ora",
    tagEn: "Now",
    done: false,
  },
];

export function EngineeringProfileHeroVisual({ isItalian }: EngineeringProfileHeroVisualProps) {
  return (
    <div className="relative w-full max-w-[340px] md:max-w-[360px] xl:max-w-[380px] mx-auto pb-6 pointer-events-none select-none">
      {/* Ambient glow */}
      <div className="absolute top-1/3 right-0 w-52 h-52 rounded-full bg-primary/8 blur-3xl" />

      {/* Main card */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1, duration: 0.7 }}
        className="relative rounded-2xl border border-primary/20 bg-surface shadow-[0_12px_48px_hsl(var(--primary)/0.15)] p-5"
      >
        {/* Profile header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-primary/30 shadow-[0_0_14px_hsl(var(--primary)/0.30)]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/about/dario-portrait-9x16.webp"
              alt="Dario Marco Bellini"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-semibold text-foreground block">Dario Marco Bellini</span>
            <span className="text-[11px] text-muted-foreground">Full Stack Developer</span>
          </div>
          <span className="text-[9px] font-mono text-muted-foreground/55 shrink-0">5+ {isItalian ? "anni" : "yrs"}</span>
        </div>

        {/* Career timeline */}
        <div className="mb-4">
          <span className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.12em] block mb-2">
            {isItalian ? "Percorso" : "Career"}
          </span>
          <div className="space-y-0">
            {timeline.map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 + i * 0.15 }}
                className="flex items-stretch gap-3"
              >
                {/* Connector */}
                <div className="flex flex-col items-center shrink-0">
                  <div
                    className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${
                      t.done
                        ? "bg-muted-foreground/30"
                        : "bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.6)]"
                    }`}
                  />
                  {i < timeline.length - 1 && (
                    <div className="w-px flex-1 bg-muted-foreground/15 my-1" style={{ minHeight: "20px" }} />
                  )}
                </div>

                {/* Content */}
                <div className={`pb-3 flex-1 min-w-0 ${i === timeline.length - 1 ? "pb-0" : ""}`}>
                  <div className="flex items-center gap-2 mb-0.5">
                    <span
                      className={`text-[11px] font-semibold ${
                        t.done ? "text-foreground/60" : "text-foreground"
                      }`}
                    >
                      {isItalian ? t.companyIt : t.companyEn}
                    </span>
                    <span
                      className={`text-[8px] font-mono rounded px-1.5 py-0.5 leading-none ${
                        t.done
                          ? "bg-surface-hover text-muted-foreground/50"
                          : "bg-primary/15 text-primary"
                      }`}
                    >
                      {isItalian ? t.tagIt : t.tagEn}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[9px] text-muted-foreground/55 font-mono">
                      {isItalian ? t.yearIt : t.yearEn}
                    </span>
                    <span className="text-[9px] text-muted-foreground/40">·</span>
                    <span className="text-[9px] text-muted-foreground/55">
                      {isItalian ? t.roleIt : t.roleEn}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Stack bars */}
        <div className="border-t border-[hsl(var(--border))] pt-3">
          <span className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.12em] block mb-2">
            {isItalian ? "Stack principale" : "Core stack"}
          </span>
          <div className="space-y-1.5">
            {stack.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.55 + i * 0.06 }}
                className="flex items-center gap-2"
              >
                <span className="text-[10px] text-muted-foreground/70 font-mono w-20 shrink-0">
                  {s.label}
                </span>
                <div className="flex-1 h-1.5 bg-surface-hover rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(s.level / 5) * 100}%` }}
                    transition={{ delay: 0.7 + i * 0.06, duration: 0.5, ease: "easeOut" }}
                    className="h-full rounded-full bg-primary"
                    style={{ opacity: 0.5 + (s.level / 5) * 0.5 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Floating chip */}
      <motion.div
        initial={{ opacity: 0, x: 10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.0 }}
        className="absolute bottom-0 right-4 flex items-center gap-1.5 bg-primary/10 border border-primary/20 rounded-full px-3 py-1.5"
      >
        <Zap className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[11px] font-semibold text-primary whitespace-nowrap">
          {isItalian ? "Enterprise → Freelance" : "Enterprise → Freelance"}
        </span>
      </motion.div>

      {/* Floating chip left */}
      <motion.div
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 1.15 }}
        className="absolute bottom-0 left-4 flex items-center gap-1.5 bg-surface border border-[hsl(var(--border))] rounded-full px-3 py-1.5 shadow-md"
      >
        <ArrowRight className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[11px] font-semibold text-foreground whitespace-nowrap">
          {isItalian ? "Prodotto & delivery" : "Product & delivery"}
        </span>
      </motion.div>
    </div>
  );
}
